/**
 * Datas civis ("YYYY-MM-DD") sem fuso, para prazos acadêmicos.
 * Instantes (reuniões, envios) são ISO-8601 UTC. "Hoje" é sempre calculado
 * no timezone IANA da instituição.
 */

export type IsoDate = string;

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string') return false;
  const m = ISO_DATE_RE.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

function parse(date: IsoDate): Date {
  if (!isIsoDate(date)) throw new RangeError(`Data inválida: ${String(date)}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function format(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = parse(date);
  d.setUTCDate(d.getUTCDate() + days);
  return format(d);
}

export function addWeeks(date: IsoDate, weeks: number): IsoDate {
  return addDays(date, weeks * 7);
}

/** Soma meses mantendo o dia quando possível (31/01 + 1 mês = 28 ou 29/02). */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const d = parse(date);
  const day = d.getUTCDate();
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return format(target);
}

/** Soma anos (29/02 → 28/02 em anos não bissextos). */
export function addYears(date: IsoDate, years: number): IsoDate {
  return addMonths(date, years * 12);
}

/**
 * Fim da retenção de arquivos de um trabalho: conclusão/cancelamento + anos da política;
 * enquanto em andamento, o término previsto serve de estimativa (recalculada no encerramento).
 */
export function fileRetentionUntil(work: { closedAt: IsoDate | null; expectedEndDate: IsoDate }, retentionYears: number): IsoDate {
  return addYears(work.closedAt ?? work.expectedEndDate, retentionYears);
}

export function diffDays(later: IsoDate, earlier: IsoDate): number {
  return Math.round((parse(later).getTime() - parse(earlier).getTime()) / 86_400_000);
}

/** Metade do período (marco de meio do prazo): início + metade dos dias até o fim. */
export function halfwayDate(start: IsoDate, end: IsoDate): IsoDate {
  return addDays(start, Math.floor(diffDays(end, start) / 2));
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function maxDate(a: IsoDate, b: IsoDate): IsoDate {
  return a >= b ? a : b;
}

/** Data civil corrente em um timezone IANA (ex.: "America/Sao_Paulo"). */
export function todayInTimeZone(timeZone: string, now: Date = new Date()): IsoDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Dia da semana (0 = domingo) e hora local em um timezone. */
export function localClock(timeZone: string, now: Date = new Date()): { weekday: number; hour: number; date: IsoDate } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const wd = parts.find((p) => p.type === 'weekday')?.value ?? 'Sun';
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(wd);
  return { weekday, hour, date: todayInTimeZone(timeZone, now) };
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Converte Date (coluna @db.Date do Prisma, meia-noite UTC) para IsoDate. */
export function toIsoDate(d: Date | string): IsoDate {
  if (typeof d === 'string') return d.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

/** Converte IsoDate para Date à meia-noite UTC (para colunas @db.Date). */
export function fromIsoDate(date: IsoDate): Date {
  return parse(date);
}

export function formatDateBR(date: IsoDate | null | undefined): string {
  if (!date || !isIsoDate(date)) return '—';
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}
